import React, { useState, useRef, useEffect } from 'react';
import { Card } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Checkbox } from '@/components/ui/checkbox';
import { formatValue } from './utils';
import { formatWallName } from '@/utils/roomDrawingFunctions';

const WallElevations = ({ floorPlanData, containerWidth = 1200, containerHeight = 400 }) => {
  const [selectedWalls, setSelectedWalls] = useState({});
  const [showDimensions, setShowDimensions] = useState(true);
  // const canvasRef = useRef(null);
  // const [canvasSize, setCanvasSize] = useState({ width: containerWidth, height: containerHeight });
  const [error, setError] = useState(null);
  const MIN_CANVAS_WIDTH = 320;

  // Create a ref for each wall's canvas
  const canvasRefs = useRef({});
  const [canvasSizes, setCanvasSizes] = useState({});

  // State for dragging arrows
  const [isDragging, setIsDragging] = useState(false);
  const [draggedArrow, setDraggedArrow] = useState(null);
  const [arrowOffsets, setArrowOffsets] = useState({});
  const [mouseStartPos, setMouseStartPos] = useState({ x: 0, y: 0 });
  // Ref to store arrow positions for hit testing
  const arrowsRef = useRef({});

  const parseData = (data) => {
    if (typeof data === 'string') {
      try {
        return JSON.parse(data);
      } catch (err) {
        console.error('Error parsing data:', err);
        setError('Invalid data format');
        return null;
      }
    }
    return data;
  };

  // useEffect(() => {
  //   const parsedData = parseData(floorPlanData);
  //   console.log('WallElevations - Initial Props:', {
  //     floorPlanData: parsedData,
  //     containerWidth,
  //     containerHeight
  //   });
  // }, []);

  useEffect(() => {
    const parsedData = parseData(floorPlanData);
    if (!parsedData) return;

    const wallsWithElements = new Set(
      Object.entries(parsedData.wallElements || {})
        .filter(([_, elements]) => elements && elements.length > 0)
        .map(([wall]) => wall)
    );
    
    setSelectedWalls(wallsWithElements);
  }, [floorPlanData]);

  // useEffect(() => {
  //   const updateCanvasSize = () => {
  //     if (canvasRef.current) {
  //       const canvas = canvasRef.current;
  //       const parentWidth = canvas.parentElement.offsetWidth;
  //       const aspectRatio = 16/9;

  //       let newWidth = Math.max(MIN_CANVAS_WIDTH, Math.min(parentWidth, containerWidth));
  //       let newHeight = newWidth / aspectRatio;

  //       if (newHeight > containerHeight) {
  //         newHeight = containerHeight;
  //         newWidth = newHeight * aspectRatio;
  //       }

  //       console.log('Canvas size updated:', { newWidth, newHeight });
  //       setCanvasSize({ width: newWidth, height: newHeight });
  //     }
  //   };

  //   updateCanvasSize();
  //   window.addEventListener('resize', updateCanvasSize);
  //   return () => window.removeEventListener('resize', updateCanvasSize);
  // }, [containerWidth, containerHeight]);

  useEffect(() => {
    const updateCanvasSizes = () => {
      const parsedData = parseData(floorPlanData);
      if (!parsedData) return;

      const availableWalls = getAvailableWalls();
      const wallCount = selectedWalls.size || 1;
      const aspectRatio = 16/9;

      const sizes = {};
      availableWalls.forEach(wall => {
        if (!canvasRefs.current[wall]) return;

        const canvas = canvasRefs.current[wall];
        const parentWidth = canvas.parentElement.offsetWidth;
        let newWidth = Math.max(MIN_CANVAS_WIDTH, Math.min(parentWidth, containerWidth));
        let newHeight = newWidth / aspectRatio;

        if (newHeight > containerHeight) {
          newHeight = containerHeight;
          newWidth = newHeight * aspectRatio;
        }

        sizes[wall] = { width: newWidth, height: newHeight };
      });

      setCanvasSizes(sizes);
    };

    updateCanvasSizes();
    window.addEventListener('resize', updateCanvasSizes);
    return () => window.removeEventListener('resize', updateCanvasSizes);
  }, [containerWidth, containerHeight, selectedWalls]);

  // useEffect(() => {
  //   const parsedData = parseData(floorPlanData);
  //   if (!parsedData || !selectedWalls) {
  //     return;
  //   }
  //   drawWallElevation(parsedData);
  // }, [selectedWalls, canvasSizes, showDimensions]);

  const drawWallAndElements = (ctx, parsedFloorPlan, startX, startY, scale, wall) => {
    const { walls, wallElements, unit, wallCoordinates } = parsedFloorPlan;
    const wallWidth = walls[wall];
    const wallHeight = wallCoordinates[wall].height;

    console.log('Drawing wall:', selectedWalls, "floorplan:", parsedFloorPlan);

    // Draw grid
    ctx.strokeStyle = '#E5E5E5';
    ctx.lineWidth = 0.5;
    const gridSize = 12;

    // Draw vertical grid lines
    for (let x = startX % (gridSize * scale); x < ctx.canvas.width; x += gridSize * scale) {
      ctx.beginPath();
      ctx.moveTo(x, 0);
      ctx.lineTo(x, ctx.canvas.height);
      ctx.stroke();
    }

    // Draw horizontal grid lines
    for (let y = startY % (gridSize * scale); y < ctx.canvas.height; y += gridSize * scale) {
      ctx.beginPath();
      ctx.moveTo(0, y);
      ctx.lineTo(ctx.canvas.width, y);
      ctx.stroke();
    }

    // Draw wall outline
    ctx.fillStyle = 'lightgray';
    ctx.strokeStyle = 'black';
    ctx.lineWidth = 2;
    ctx.fillRect(startX, startY, wallWidth * scale, wallHeight * scale);
    ctx.strokeRect(startX, startY, wallWidth * scale, wallHeight * scale);

    // Draw wall elements
    const elements = wallElements?.[wall] || [];
    for (const element of elements) {
      const { type, x, width, height } = element;
      const elementX = startX + x * scale;
      const elementY = startY + (wallHeight - height - element.y) * scale;

      switch (type) {
        case 'window':
          ctx.fillStyle = 'lightblue';
          ctx.fillRect(elementX, elementY, width * scale, height * scale);
          ctx.strokeStyle = '#87CEEB';
          ctx.lineWidth = 3;
          ctx.strokeRect(elementX, elementY, width * scale, height * scale);
          break;
        case 'door':
          ctx.fillStyle = 'brown';
          ctx.fillRect(elementX, elementY, width * scale, height * scale);
          ctx.strokeStyle = '#5C4033';
          ctx.lineWidth = 3;
          ctx.strokeRect(elementX, elementY, width * scale, height * scale);
          break;
        case 'opening':
          ctx.fillStyle = 'white';
          ctx.fillRect(elementX, elementY, width * scale, height * scale);
          break;
        case 'electricity':
        case 'water':
        case 'gas':
          const radius = Math.min(width, height) * scale / 2;
          ctx.beginPath();
          ctx.arc(elementX + radius, elementY + radius, radius, 0, Math.PI * 2);
          ctx.fillStyle = type === 'electricity' ? 'yellow' : type === 'water' ? 'blue' : 'red';
          ctx.fill();
          ctx.stroke();
          break;
        default:
          console.warn(`Unknown element type: ${type}`);
          break;
      }
    }

    return { elements, wallWidth, wallHeight };
  };


  const isPointNearLine = (x, y, x1, y1, x2, y2, threshold = 5) => {
    // For vertical lines
    if (Math.abs(x1 - x2) < threshold) {
      return Math.abs(x - x1) < threshold && 
             y >= Math.min(y1, y2) - threshold && 
             y <= Math.max(y1, y2) + threshold;
    }
    // For horizontal lines
    if (Math.abs(y1 - y2) < threshold) {
      return Math.abs(y - y1) < threshold && 
             x >= Math.min(x1, x2) - threshold && 
             x <= Math.max(x1, x2) + threshold;
    }
    return false;
  };

  const handleMouseUp = () => {
    setIsDragging(false);
    setDraggedArrow(null);
  };

// New handleMouseDown
const handleMouseDown = (e, wall) => {
  if (!showDimensions || !canvasRefs.current[wall]) return;

  const rect = canvasRefs.current[wall].getBoundingClientRect();
  const x = e.clientX - rect.left;
  const y = e.clientY - rect.top;

  const wallArrows = arrowsRef.current[wall] || [];
  const hitArrow = wallArrows.find(arrow => 
    isPointNearLine(x, y, arrow.x1, arrow.y1, arrow.x2, arrow.y2)
  );

  if (hitArrow) {
    setIsDragging(true);
    setDraggedArrow({ wall, id: hitArrow.id });
    setMouseStartPos({ x, y });

    if (!arrowOffsets[`${wall}_${hitArrow.id}`]) {
      setArrowOffsets(prev => ({
        ...prev,
        [`${wall}_${hitArrow.id}`]: { x: 0, y: 0 }
      }));
    }
  }
};

// New handleMouseMove
const handleMouseMove = (e, wall) => {
  if (!isDragging || !draggedArrow || !canvasRefs.current[wall]) return;
  
  // Only handle mouse move for the wall being dragged
  if (draggedArrow.wall !== wall) return;

  const rect = canvasRefs.current[wall].getBoundingClientRect();
  const x = e.clientX - rect.left;
  const y = e.clientY - rect.top;

  const wallArrows = arrowsRef.current[wall] || [];
  const arrow = wallArrows.find(a => a.id === draggedArrow.id);
  if (!arrow) return;

  const dx = x - mouseStartPos.x;
  const dy = y - mouseStartPos.y;

  const arrowKey = `${wall}_${draggedArrow.id}`;

  setArrowOffsets(prev => ({
    ...prev,
    [arrowKey]: {
      x: Math.abs(arrow.y1 - arrow.y2) < 5 ? prev[arrowKey]?.x || 0 : (prev[arrowKey]?.x || 0) + dx,
      y: Math.abs(arrow.y1 - arrow.y2) < 5 ? (prev[arrowKey]?.y || 0) + dy : prev[arrowKey]?.y || 0
    }
  }));

  setMouseStartPos({ x, y });
  
  // Redraw only the affected wall
  const parsedData = parseData(floorPlanData);
  if (parsedData) {
    drawWallElevation(wall, parsedData);
  }
};

  const drawArrowWithLabel = (ctx, x1, y1, x2, y2, label, arrowSize, arrowId, wall, isDouble = false) => {
    // const offset = arrowOffsets[arrowId] || { x: 0, y: 0 };
    const offset = arrowOffsets[`${wall}_${arrowId}`] || { x: 0, y: 0 };
    const isVertical = Math.abs(x1 - x2) < 5;

    // Apply offsets based on arrow orientation
    const adjustedX = isVertical ? x1 + offset.x : x1;
    const adjustedX2 = isVertical ? x2 + offset.x : x2;
    const adjustedY = isVertical ? y1 : y1 + offset.y;
    const adjustedY2 = isVertical ? y2 : y2 + offset.y;


    // Store arrow position for hit testing
    // New store arrow position
    if (!arrowsRef.current[wall]) {
      arrowsRef.current[wall] = [];
    }
    arrowsRef.current[wall].push({
      id: arrowId,
      x1: adjustedX,
      y1: adjustedY,
      x2: adjustedX2,
      y2: adjustedY2,
      isVertical
    });

    // Draw the arrow line
    ctx.beginPath();
    ctx.moveTo(adjustedX, adjustedY);
    ctx.lineTo(adjustedX2, adjustedY2);
    ctx.stroke();

    if (isDouble) {
      // Draw start arrowhead for double arrows
      ctx.beginPath();
      ctx.moveTo(adjustedX, adjustedY);
      if (isVertical) {
        ctx.lineTo(adjustedX - arrowSize/2, adjustedY + arrowSize);
        ctx.lineTo(adjustedX + arrowSize/2, adjustedY + arrowSize);
      } else {
        ctx.lineTo(adjustedX + arrowSize, adjustedY - arrowSize/2);
        ctx.lineTo(adjustedX + arrowSize, adjustedY + arrowSize/2);
      }
      ctx.closePath();
      ctx.fill();
    }

    // Draw end arrowhead
    ctx.beginPath();
    ctx.moveTo(adjustedX2, adjustedY2);
    if (isVertical) {
      ctx.lineTo(adjustedX2 - arrowSize/2, adjustedY2 + (isDouble ? -arrowSize : arrowSize));
      ctx.lineTo(adjustedX2 + arrowSize/2, adjustedY2 + (isDouble ? -arrowSize : arrowSize));
    } else {
      ctx.lineTo(adjustedX2 - arrowSize, adjustedY2 - arrowSize/2);
      ctx.lineTo(adjustedX2 - arrowSize, adjustedY2 + arrowSize/2);
    }
    ctx.closePath();
    ctx.fill();

    // Draw label with background
    const metrics = ctx.measureText(label);
    const textHeight = metrics.actualBoundingBoxAscent + metrics.actualBoundingBoxDescent;

    if (isVertical) {
      ctx.save();
      ctx.translate(adjustedX + 3, adjustedY2 + (adjustedY - adjustedY2)/2);
      ctx.rotate(-Math.PI/2);

      ctx.fillStyle = 'rgba(255, 255, 255, 0.8)';
      ctx.fillRect(
        -metrics.width/2 - 2,
        -textHeight,
        metrics.width + 4,
        textHeight + 4
      );

      ctx.fillStyle = 'black';
      ctx.fillText(label, 0, 2);
      ctx.restore();
    } else {
      ctx.fillStyle = 'rgba(255, 255, 255, 0.8)';
      ctx.fillRect(
        adjustedX + (adjustedX2 - adjustedX)/2 - metrics.width/2 - 2,
        adjustedY - textHeight - 2,
        metrics.width + 4,
        textHeight + 4
      );

      ctx.fillStyle = 'black';
      ctx.fillText(label, adjustedX + (adjustedX2 - adjustedX)/2, adjustedY + textHeight/2);
    }
  };

  // Modified drawDimensions function to include unique IDs for arrows
  const drawDimensions = (ctx, elements, wallWidth, wallHeight, startX, startY, scale, unit, wall) => {
    // Clear previous arrows reference
    // arrowsRef.current = [];
    arrowsRef.current[wall] = [];

    const arrowSize = 8;
    ctx.strokeStyle = 'blue';
    ctx.fillStyle = 'black';
    ctx.lineWidth = 1;
    ctx.font = '12px Arial';
    ctx.textAlign = 'center';

    // Draw element dimensions
    elements.forEach((element, index) => {
      const { type, x, width, height } = element;
      const elementX = startX + x * scale;
      const elementY = startY + (wallHeight - height - element.y) * scale;

      // Position dimensions
      if (element.x !== 0) {
        drawArrowWithLabel(
          ctx, 
          startX, 
          elementY + height * scale,
          elementX, 
          elementY + height * scale,
          `${formatValue(x, unit)} ${unit}`,
          arrowSize,
          `elem_x_${index}`,
          wall
        );
      }

      if (element.y !== 0) {
        drawArrowWithLabel(
          ctx,
          elementX,
          startY + wallHeight * scale,
          elementX,
          elementY + height * scale,
          `${formatValue(element.y, unit)} ${unit}`,
          arrowSize,
          `elem_y_${index}`,
          wall
        );
      }

      // Element dimensions for non-utility elements
      if (!['electricity', 'water', 'gas'].includes(type)) {
        // Width dimension
        drawArrowWithLabel(
          ctx,
          elementX,
          elementY - 15,
          elementX + width * scale,
          elementY - 15,
          `${formatValue(width, unit)} ${unit}`,
          arrowSize,
          `elem_width_${index}`,
          wall,
          true  // Double arrow
        );

        // Height dimension
        drawArrowWithLabel(
          ctx,
          elementX + width * scale + 15,
          elementY,
          elementX + width * scale + 15,
          elementY + height * scale,
          `${formatValue(height, unit)} ${unit}`,
          arrowSize,
          `elem_height_${index}`,
          wall,
          true  // Double arrow
        );
      }
    });

    // Wall dimensions
    drawArrowWithLabel(
      ctx,
      startX,
      startY + wallHeight * scale + 20,
      startX + wallWidth * scale,
      startY + wallHeight * scale + 20,
      `${formatValue(wallWidth, unit)} ${unit}`,
      arrowSize,
      'wall_width',
      wall,
      true  // Double arrow
    );

    drawArrowWithLabel(
      ctx,
      startX - 20,
      startY,
      startX - 20,
      startY + wallHeight * scale,
      `${formatValue(wallHeight, unit)} ${unit}`,
      arrowSize,
      'wall_height',
      wall,
      true  // Double arrow
    );
  };

  const drawWallElevation = async (wall, parsedFloorPlan) => {
    if (!canvasRefs.current[wall] || !parsedFloorPlan) {
      return;
    }

    const canvas = canvasRefs.current[wall];
    const ctx = canvas.getContext('2d');
    const canvasSize = canvasSizes[wall];
    if (!canvasSize) return;

    // Clear canvas
    ctx.fillStyle = 'white';
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    // Calculate scale and starting position
    const padding = 40;
    const scaleX = (canvas.width - padding * 2) / parsedFloorPlan.walls[wall];
    const scaleY = (canvas.height - padding * 2) / parsedFloorPlan.wallCoordinates[wall].height;
    const scale = Math.min(scaleX, scaleY);

    // Calculate starting position to center the drawing
    const startX = (canvas.width - parsedFloorPlan.walls[wall] * scale) / 2;
    const startY = (canvas.height - parsedFloorPlan.wallCoordinates[wall].height * scale) / 2;

    // First draw the wall and elements
    const { elements, wallWidth, wallHeight } = drawWallAndElements(ctx, parsedFloorPlan, startX, startY, scale, wall);

    // Then draw dimensions if enabled
    if (showDimensions) {
      drawDimensions(ctx, elements, wallWidth, wallHeight, startX, startY, scale, parsedFloorPlan.unit, wall);
    }
  };

  // Get available walls based on room type
  const getAvailableWalls = () => {
    const parsedData = parseData(floorPlanData);
    if (!parsedData?.wallCoordinates) {
      console.log('No wall coordinates available');
      return [];
    }
    const walls = Object.keys(parsedData.wallCoordinates);
    // console.log('Available walls:', walls);
    return walls;
  };

  // const handleWallSelect = (wall) => {
  //   console.log('Wall selected:', wall);
  //   setSelectedWall(wall);
  // };

  const handleWallToggle = (wall) => {
    setSelectedWalls(prev => {
      const newSelection = new Set(prev);
      if (newSelection.has(wall)) {
        newSelection.delete(wall);
      } else {
        newSelection.add(wall);
      }
      return newSelection;
    });
  };

  useEffect(() => {
    const parsedData = parseData(floorPlanData);
    if (!parsedData) return;

    Array.from(selectedWalls).forEach(wall => {
      drawWallElevation(wall, parsedData);
    });
  }, [selectedWalls, canvasSizes, showDimensions]);

  return (
    <div className="w-full space-y-4">
      {error && (
        <div className="text-red-500 mb-2 text-sm">{error}</div>
      )}
      
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div className="flex items-center gap-4 flex-wrap">
          <Label>Select Walls:</Label>
          <div className="flex gap-4 flex-wrap">
            {getAvailableWalls().map(wall => (
              <div key={wall} className="flex items-center space-x-2">
                <Checkbox
                  id={`wall-${wall}`}
                  checked={Array.from(selectedWalls).includes(wall)}
                  onCheckedChange={() => handleWallToggle(wall)}
                />
                <Label htmlFor={`wall-${wall}`}>
                  Wall-{formatWallName(wall)}
                </Label>
              </div>
            ))}
          </div>
        </div>
        
        <div className="flex items-center gap-2">
          <Label>Show Dimensions:</Label>
          <Switch
            checked={showDimensions}
            onCheckedChange={setShowDimensions}
          />
        </div>
      </div>
      
      <div className="grid grid-cols-1 gap-4">
        {Array.from(selectedWalls).map(wall => (
          <Card key={wall} className="p-4">
            <div className="font-medium mb-2">Wall-{formatWallName(wall)}</div>
            <canvas
              ref={el => canvasRefs.current[wall] = el}
              width={canvasSizes[wall]?.width || containerWidth}
              height={canvasSizes[wall]?.height || containerHeight}
              className="border w-full h-auto"
              style={{
                maxWidth: `${canvasSizes[wall]?.width || containerWidth}px`,
                maxHeight: `${canvasSizes[wall]?.height || containerHeight}px`,
                cursor: isDragging ? 'grabbing' : 'default'
              }}
              onMouseDown={(e) => handleMouseDown(e, wall)}
              onMouseMove={(e) => handleMouseMove(e, wall)}
              onMouseUp={handleMouseUp}
              onMouseLeave={handleMouseUp}
            />
          </Card>
        ))}
      </div>
    </div>
  );
}

export default WallElevations;